import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { ProjectElementInfo } from 'common/types'

const initialState: ProjectElementInfo[] = []
export const treeObjectsSlice = createSlice({
  name: 'treeObjects',
  initialState,
  reducers: {
    addTreeElementsByParentId(
      state,
      action: PayloadAction<{ parentId?: string; nodes: ProjectElementInfo[] }>,
    ) {
      const { parentId, nodes } = action.payload
      if (!parentId) {
        updateNodes(state, nodes)
      } else {
        const parent = findNode(parentId, state)
        if (parent) {
          updateNodes(parent.children, nodes)
        }
      }
    },
    addNewNodes(state, action: PayloadAction<ProjectElementInfo[]>) {
      const nodes = action.payload
      nodes.forEach(node => {
        const parentId = node.parentUid
        if (!parentId) {
          updateNodes(state, [node])
        } else {
          const parent = findNode(parentId, state)
          if (parent) {
            updateNodes(parent.children, [node])
          }
        }
      })
    },
    updateExistingNodes(state, action: PayloadAction<ProjectElementInfo[]>) {
      const nodes = action.payload
      nodes.forEach(node => {
        const parent = findNode(node.parentUid, state)
        if (parent) {
          updateNodes(parent.children, [node])
        }
      })
    },
    deleteNodes(state, action: PayloadAction<ProjectElementInfo[]>) {
      const nodes = action.payload
      nodes.forEach(node => {
        const parent = findNode(node.parentUid, state)
        if (parent) {
          const removeIndex = parent.children.findIndex(parentItem => parentItem.uid === node.uid)
          parent.children.splice(removeIndex, 1)
        }
      })
    },
    clearTreeElements() {
      return initialState
    },
  },
})

const updateNodes = (
  oldNodes: Draft<ProjectElementInfo>[],
  newNodes: ProjectElementInfo[],
): void => {
  const oldNodeIds = oldNodes.map(node => node.uid)
  const addedNodes = newNodes.filter(node => !oldNodeIds.includes(node.uid))
  const updatedNodes = newNodes.filter(node => oldNodeIds.includes(node.uid))

  for (let i = 0; i < oldNodes.length; ++i) {
    const updatedNode = updatedNodes.find(node => node.uid === oldNodes[i].uid)
    if (updatedNode) {
      if (!updatedNode.children.length) {
        updatedNode.children = oldNodes[i].children
      }
      updatedNode.disabled = updatedNode.disabled ?? oldNodes[i].disabled
      oldNodes[i] = updatedNode
    }
  }
  oldNodes.push(...addedNodes)
  oldNodes.sort((n1, n2) => n1.orderNum - n2.orderNum)
}

const findNode = (
  nodeId: string | undefined,
  nodes: Draft<ProjectElementInfo>[],
): Draft<ProjectElementInfo> | undefined => {
  for (const node of nodes) {
    if (node.uid === nodeId) {
      return node
    }

    const child = findNode(nodeId, node.children)
    if (child) {
      return child
    }
  }

  return
}

export default treeObjectsSlice.reducer
